<script>
	import { onMount } from 'svelte';
	import { Circle2 } from 'svelte-loading-spinners';
	import { fetchTopList, displayInt } from '../utils/utils.js';

	let allBrfs = [];
	let filteredBrfs = [];
	let topBrfs = [];
	let bottomBrfs = [];
	let loading = true;
	let error = '';

	// Filter states
	let sizeFilter = 'all';
	let ageFilter = 'all';

	// Helper function to create URL-friendly slugs from BRF names
	function slugify(text) {
		if (!text) return '';
		return text.toString().toLowerCase()
			.replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
			.replace(/[^\w\-]+/g, '-').replace(/\-\-+/g, '-')
			.replace(/^-+/, '').replace(/-+$/, '');
	}

	// Function to create BRF link
	function createBrfLink(id, name) {
		const slug = slugify(name);
		return `https://boanalys.io/brf/${id}/${slug}`;
	}

	onMount(async () => {
		await loadBrfList();
	});

	async function loadBrfList() {
		loading = true;
		error = '';

		try {
			allBrfs = await fetchTopList();
			applyFilters();
		} catch (err) {
			console.error('Error fetching BRF list:', err);
			error = 'Kunde inte ladda BRF-listan. Försök igen senare.';
		} finally {
			loading = false;
		}
	}

	function applyFilters() {
		const filtered = allBrfs.filter(brf => {
			// Size filter
			let passesSize = true;
			if (sizeFilter === 'small') {
				passesSize = brf.numberOfApartments < 30;
			} else if (sizeFilter === 'medium') {
				passesSize = brf.numberOfApartments >= 30 && brf.numberOfApartments <= 70;
			} else if (sizeFilter === 'large') {
				passesSize = brf.numberOfApartments > 70;
			}

			// Age filter
			let passesAge = true;
			if (ageFilter !== 'all') {
				if (ageFilter === 'old') {
					passesAge = brf.foundingYear && brf.foundingYear <= 1959;
				} else if (ageFilter === 'middle') {
					passesAge = brf.foundingYear && brf.foundingYear >= 1960 && brf.foundingYear <= 2000;
				} else if (ageFilter === 'new') {
					passesAge = brf.foundingYear && brf.foundingYear >= 2001;
				}
			}

			return passesSize && passesAge;
		});

		// Sort by rating
		const sortedByRating = [...filtered].sort((a, b) => b.rating - a.rating);
		
		// Separate into high performers (80+) and low performers (40-)
		// Take up to 10 from each category, but don't force it
		topBrfs = sortedByRating.filter(brf => brf.rating >= 80).slice(0, 10);
		
		// For bottom BRFs, sort from worst to best (lowest rating first)
		const bottomCandidates = filtered.filter(brf => brf.rating <= 40);
		bottomBrfs = bottomCandidates.sort((a, b) => a.rating - b.rating).slice(0, 10);

		filteredBrfs = [...topBrfs, ...bottomBrfs];
	}

	function getRatingColor(rating) {
		if (rating >= 80) return '#28a745'; // Green
		if (rating >= 60) return '#ffc107'; // Yellow
		if (rating >= 40) return '#fd7e14'; // Orange
		return '#dc3545'; // Red
	}

	function getRatingBarWidth(rating) {
		return Math.max(5, (rating / 100) * 100); // Minimum 5% width for visibility
	}

	// Reactive filtering - update when filters or data changes
	$: if (allBrfs.length > 0) {
		applyFilters();
	}

	// React to filter changes
	$: if (sizeFilter || ageFilter) {
		if (allBrfs.length > 0) {
			applyFilters();
		}
	}
</script>

<div class="page-container">
	{#if loading}
		<div style="display: flex; justify-content: center; align-items: center; min-height: 50vh;">
			<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
		</div>
	{:else if error}
		<div class="content-box" style="margin-top: 10vh; max-width: 800px; text-align: center;">
			<p class="error-message">{error}</p>
		</div>
	{:else}
		<div class="content-container">
			<!-- Header -->
			<div class="header-section">
				<h1><i class="fa-solid fa-building fa-sm icon-detail" style="padding-right: 8px;"></i>Topplista över bostadsrättsföreningar</h1>
				<p class="subtitle">Jämför och utforska bostadsrättsföreningar baserat på vårt betygsystem. Se de bästa och sämsta föreningarna.</p>
			</div>

			<!-- Filters -->
			<div class="filters-section">
				<div class="filter-card-wrapper">
					<label for="size-filter">Storlek</label>
					<select id="size-filter" bind:value={sizeFilter} class="filter-select">
						<option value="all">Alla</option>
						<option value="small">Små (&lt; 30 lgh)</option>
						<option value="medium">Medelstora (30-70 lgh)</option>
						<option value="large">Stora (&gt; 70 lgh)</option>
					</select>
				</div>
				
				<div class="filter-card-wrapper">
					<label for="age-filter">Ålder</label>
					<select id="age-filter" bind:value={ageFilter} class="filter-select">
						<option value="all">Alla</option>
						<option value="old">Byggda före 1960</option>
						<option value="middle">Byggda 1960-2000</option>
						<option value="new">Byggda efter 2000</option>
					</select>
				</div>
			</div>

			<!-- Two Column Layout -->
			<div class="two-column-layout">
				<!-- Top 10 BRFs -->
				<div class="column">
					<div class="column-header">
						<h2><i class="fa-solid fa-trophy"></i>Topp 10</h2>
						<p class="column-subtitle">Högst betyg baserat på vårt betygsystem</p>
					</div>
					<div class="brf-list">
						{#each topBrfs as brf, index}
							<a href={createBrfLink(brf.id, brf.name)} class="brf-card" target="_blank" rel="noopener noreferrer">
								<!-- Rating Bar -->
								<div class="rating-bar-container">
									<div 
										class="rating-bar" 
										style="width: {getRatingBarWidth(brf.rating)}%; background-color: {getRatingColor(brf.rating)}"
									></div>
								</div>

								<!-- Card Header -->
								<div class="card-header">
									<h3 class="brf-name">{brf.name}</h3>
									<div class="rating-badge">
										<span class="rating-value" style="color: {getRatingColor(brf.rating)}">{brf.rating}</span>
									</div>
								</div>

								<!-- Key Metrics -->
								<div class="key-metrics">
									<div class="metric">
										<span class="metric-label">Lägenheter</span>
										<span class="metric-value">{brf.numberOfApartments} st</span>
									</div>
									<div class="metric">
										<span class="metric-label">Avgift/kvm</span>
										<span class="metric-value">{brf.feePerSqm ? displayInt(brf.feePerSqm) : '–'} kr</span>
									</div>
									{#if brf.foundingYear}
									<div class="metric">
										<span class="metric-label">Byggår</span>
										<span class="metric-value">{brf.foundingYear}</span>
									</div>
									{/if}
								</div>
							</a>
						{/each}
					</div>
				</div>

				<!-- Bottom 10 BRFs -->
				<div class="column">
					<div class="column-header">
						<h2><i class="fa-solid fa-triangle-exclamation"></i>Sämsta 10</h2>
						<p class="column-subtitle">Föreningar med utvecklingspotential</p>
					</div>
					<div class="brf-list">
						{#each bottomBrfs as brf}
							<a href={createBrfLink(brf.id, brf.name)} class="brf-card" target="_blank" rel="noopener noreferrer">
								<!-- Rating Bar -->
								<div class="rating-bar-container">
									<div 
										class="rating-bar" 
										style="width: {getRatingBarWidth(brf.rating)}%; background-color: {getRatingColor(brf.rating)}"
									></div>
								</div>

								<!-- Card Header -->
								<div class="card-header">
									<h3 class="brf-name">{brf.name}</h3>
									<div class="rating-badge">
										<span class="rating-value" style="color: {getRatingColor(brf.rating)}">{brf.rating}</span>
									</div>
								</div>

								<!-- Key Metrics -->
								<div class="key-metrics">
									<div class="metric">
										<span class="metric-label">Lägenheter</span>
										<span class="metric-value">{brf.numberOfApartments} st</span>
									</div>
									<div class="metric">
										<span class="metric-label">Avgift/kvm</span>
										<span class="metric-value">{brf.feePerSqm ? displayInt(brf.feePerSqm) : '–'} kr</span>
									</div>
									{#if brf.foundingYear}
									<div class="metric">
										<span class="metric-label">Byggår</span>
										<span class="metric-value">{brf.foundingYear}</span>
									</div>
									{/if}
								</div>
							</a>
						{/each}
					</div>
				</div>
			</div>

			{#if topBrfs.length === 0 && bottomBrfs.length === 0}
				<div class="no-results">
					<i class="fa-solid fa-search"></i>
					<p>Inga föreningar matchar de valda filtren</p>
					<button on:click={() => { sizeFilter = 'all'; ageFilter = 'all'; }} class="reset-filters-btn">
						Rensa filter
					</button>
				</div>
			{/if}
		</div>
	{/if}
</div>

<style>
	.page-container {
		margin-top: 100px;
		max-width: 1400px;
		margin-left: auto;
		margin-right: auto;
		padding: 0 20px;
	}

	.content-container {
		display: flex;
		flex-direction: column;
		gap: 30px;
	}

	/* Header Section */
	.header-section {
		text-align: center;
	}

	.header-section h1 {
		font-family: 'MuseoModerna', sans-serif;
		font-size: 2.2em;
		font-weight: 600;
		color: #1c1c1e;
		margin: 0 0 10px 0;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
	}

	.subtitle {
		color: #6c757d;
		font-size: 1em;
		margin: 0;
	}

	.icon-detail {
		color: #6c757d;
	}

	/* Filters Section */
	.filters-section {
		display: flex;
		align-items: stretch;
		gap: 20px;
		flex-wrap: wrap;
	}

	.filter-card-wrapper {
		background: linear-gradient(135deg, var(--box-title-color, #4f46e5), #343a40);
		color: white;
		border-radius: 8px;
		padding: 16px;
		text-align: center;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		position: relative;
		min-width: 200px;
		flex: 1;
	}

	.filter-card-wrapper label {
		display: block;
		font-weight: 600;
		color: white;
		margin-bottom: 12px;
		font-size: 0.9em;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.filter-select {
		width: 100%;
		padding: 10px 12px;
		border: 1px solid rgba(255, 255, 255, 0.3);
		border-radius: 6px;
		background-color: rgba(255, 255, 255, 0.15);
		color: white;
		font-size: 0.9em;
		backdrop-filter: blur(10px);
	}

	.filter-select:focus {
		outline: none;
		border-color: rgba(255, 255, 255, 0.6);
		box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
	}

	.filter-select option {
		background-color: #333;
		color: white;
	}

	@media (max-width: 768px) {
		.filters-section {
			flex-direction: column;
		}

		.filter-card-wrapper {
			min-width: auto;
		}
	}

	/* Two Column Layout */
	.two-column-layout {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 40px;
		margin-top: 20px;
	}

	.column {
		display: flex;
		flex-direction: column;
	}

	.column-header {
		margin-bottom: 20px;
		text-align: center;
		padding: 20px;
		background: linear-gradient(135deg, #f8f9fa, #e9ecef);
		border-radius: 12px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.column-header h2 {
		margin: 0 0 8px 0;
		color: #343a40;
		font-size: 1.3em;
		font-weight: 600;
	}

	.column-header h2 i {
		margin-right: 8px;
		color: #6c757d;
	}

	.column-subtitle {
		margin: 0;
		color: #6c757d;
		font-size: 0.9em;
	}

	.brf-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	@media (max-width: 768px) {
		.two-column-layout {
			grid-template-columns: 1fr;
			gap: 30px;
		}
	}

	/* BRF Card */
	.brf-card {
		background-color: #fff;
		border-radius: 12px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		padding: 0;
		text-decoration: none;
		color: inherit;
		transition: all 0.3s ease;
		overflow: hidden;
		position: relative;
	}

	.brf-card:hover {
		transform: translateY(-4px);
		box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
		text-decoration: none;
	}

	.rating-bar-container {
		height: 4px;
		background-color: #e9ecef;
		position: relative;
	}

	.rating-bar {
		height: 100%;
		transition: width 0.3s ease;
	}

	.card-header {
		padding: 16px 20px 12px 20px;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: 12px;
	}

	.brf-name {
		font-size: 1.1em;
		font-weight: 600;
		color: #333;
		margin: 0;
		line-height: 1.3;
		flex: 1;
	}

	.rating-badge {
		flex-shrink: 0;
	}

	.rating-value {
		font-family: 'MuseoModerno', sans-serif;
		font-size: 1.4em;
		font-weight: 600;
		line-height: 1;
	}

	/* Key Metrics */
	.key-metrics {
		padding: 0 20px 20px 20px;
		display: flex;
		justify-content: space-around;
		gap: 12px;
		flex-wrap: wrap;
	}

	.metric {
		text-align: center;
		flex: 1;
		min-width: 80px;
	}

	.metric-label {
		display: block;
		font-size: 0.75em;
		color: #6c757d;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		margin-bottom: 4px;
	}

	.metric-value {
		display: block;
		font-size: 0.9em;
		font-weight: 600;
		color: #333;
	}



	/* No Results */
	.no-results {
		text-align: center;
		padding: 60px 20px;
		color: #6c757d;
	}

	.no-results i {
		font-size: 3em;
		margin-bottom: 16px;
		opacity: 0.5;
	}

	.no-results p {
		font-size: 1.1em;
		margin: 0 0 20px 0;
	}

	.reset-filters-btn {
		background-color: #007bff;
		color: white;
		border: none;
		padding: 10px 20px;
		border-radius: 6px;
		font-size: 0.9em;
		cursor: pointer;
		transition: background-color 0.3s ease;
	}

	.reset-filters-btn:hover {
		background-color: #0056b3;
	}

	.error-message {
		color: #dc3545;
		font-size: 1.1em;
		text-align: center;
	}

	.content-box {
		background-color: #fff;
		border-radius: 12px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		padding: 20px;
	}
</style>
